/* istanbul ignore file */
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ProductMediaState } from 'src/components/organisms/ProductMediaCarousel/types';

const FADE_TRANSITION_MS = 150;
const SCROLL_SETTLE_MS = 150;
// Long enough to outlive a covering surface's close animation (the modal animates out for
// 250ms, during which react-modal's body scroll lock is still applied and a single
// scrollLeft write can be dropped or reverted by the reflow when the lock is released)
const RECONCILE_WINDOW_FRAMES = 45;

export type OnSwipeToMedia = (mediaIndex: number) => void;
type ItemReference = MutableRefObject<HTMLElement[]>;

/*
  The current slide inherently lives in two places: `currentMedia` in state, and the scroll
  position of the snap container. State is the single source of truth and the scroll position
  is a projection of it: a scroll gesture reports the slide the container comes to rest on
  into state, and any state change the container is not already resting on is projected back
  by scrolling to it. Without the projection the two drift apart the moment one write is
  missed (e.g. state changed from the full screen modal while the container was covered),
  which shows up as the counter and dots disagreeing with the visible slide.

  Two things this hook is careful about, both of which it once got wrong:

  The container is held in state rather than a ref, so every effect re-runs when the node
  itself changes. Keyed on anything else, a container that mounts later than the first render
  leaves the listener attached to a node that is no longer on the page, and swipes stop being
  reported at all.

  Nothing is projected onto a container that cannot scroll. That is the only condition under
  which a projection means anything, and it is what stands the hook down when the same slides
  are laid out as a grid, where writing scrollLeft would be a no-op the fade would still play
  around.
*/

const canScroll = (container: HTMLDivElement) => container.scrollWidth > container.clientWidth;

const getRestingMediaIndex = (container: HTMLDivElement, items: HTMLElement[]) => {
  let restingIndex = 0;
  let smallestDistance = Number.POSITIVE_INFINITY;

  items.forEach((item, index) => {
    if (!item) {
      return;
    }
    const distance = Math.abs(item.offsetLeft - container.scrollLeft);
    if (distance < smallestDistance) {
      smallestDistance = distance;
      restingIndex = index;
    }
  });

  return restingIndex;
};

export const useProductMediaCarousel = (
  onSwipeToMedia: OnSwipeToMedia,
  currentMedia: ProductMediaState['currentMedia'],
  navigatedBy: ProductMediaState['navigatedBy'],
  numberOfMedia: number,
  suspendScrollSync = false,
) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const itemRefs: ItemReference = useRef([]);
  const onSwipeToMediaRef = useRef(onSwipeToMedia);
  const currentMediaRef = useRef(currentMedia);
  const suspendScrollSyncRef = useRef(suspendScrollSync);
  const isProgrammaticScrollPending = useRef(false);
  const isReconcileWindowActive = useRef(false);

  useEffect(() => {
    onSwipeToMediaRef.current = onSwipeToMedia;
    currentMediaRef.current = currentMedia;
    suspendScrollSyncRef.current = suspendScrollSync;
  });

  const scrollToMedia = useCallback(
    (mediaIndex: number) => {
      const item = itemRefs.current[mediaIndex];

      if (!container || !item || container.scrollLeft === item.offsetLeft) {
        return;
      }

      isProgrammaticScrollPending.current = true;
      container.scrollLeft = item.offsetLeft;
      // The browser clamps the write when the container cannot reach the position (e.g. it has
      // no layout), and a clamped write may never fire a scroll event, so don't wait on one.
      if (container.scrollLeft !== item.offsetLeft) {
        isProgrammaticScrollPending.current = false;
      }
    },
    [container],
  );

  /*
    Reconcile whenever the sync is (re)enabled: on mount, and when the surface covering the
    carousel (the full screen modal) hands it back. State changes made while the sync was
    suspended were never projected into the container, so snap to the current media - and
    keep re-asserting it for a short window rather than writing once, because while the
    modal is still animating out the browser can drop or revert the write. A user gesture
    on the container ends the window immediately so it never fights a swipe. Declared
    before the projection effect below so that, in the same render, the snap runs first
    and the projection effect finds nothing left to do (no redundant fade).
  */
  useEffect(() => {
    if (suspendScrollSync || !container || !canScroll(container)) {
      return;
    }

    // Clear any fade the suspension may have interrupted mid transition
    container.style.opacity = '1';

    let framesLeft = RECONCILE_WINDOW_FRAMES;
    let frameId: number | null = null;
    isReconcileWindowActive.current = true;

    const endWindow = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      isReconcileWindowActive.current = false;
      // Any scroll our writes produced has been accounted for by ending the window, so a
      // leftover pending flag must not swallow the user's next real swipe
      isProgrammaticScrollPending.current = false;
    };

    const enforce = () => {
      scrollToMedia(currentMediaRef.current);
      if (framesLeft > 0) {
        framesLeft -= 1;
        frameId = requestAnimationFrame(enforce);
      } else {
        endWindow();
      }
    };

    enforce();

    // A gesture hands authority over the scroll position straight back to the user
    const gestureEvents = ['touchstart', 'pointerdown', 'wheel'] as const;
    gestureEvents.forEach((name) => container.addEventListener(name, endWindow, { passive: true }));

    return () => {
      endWindow();
      gestureEvents.forEach((name) => container.removeEventListener(name, endWindow));
    };
  }, [container, scrollToMedia, suspendScrollSync]);

  // State → scroll position: project the current media into the container whenever it is not
  // already resting on it. Click navigation keeps the fade transition; anything else snaps.
  useEffect(() => {
    if (suspendScrollSync || !container || !canScroll(container)) {
      return;
    }
    if (getRestingMediaIndex(container, itemRefs.current) === currentMedia) {
      return;
    }

    if (navigatedBy !== 'click') {
      scrollToMedia(currentMedia);
      return;
    }

    container.style.transition = `opacity ${FADE_TRANSITION_MS}ms`;
    container.style.opacity = '0';

    const timeoutId = setTimeout(() => {
      scrollToMedia(currentMedia);
      container.style.opacity = '1';
    }, FADE_TRANSITION_MS);

    return () => clearTimeout(timeoutId);
  }, [container, currentMedia, navigatedBy, scrollToMedia, suspendScrollSync]);

  // Scroll position → state: report the slide a scroll gesture is closest to as a swipe.
  // Reported eagerly on every scroll event so the counter and dots track the gesture as it
  // happens, with a debounced settle pass as the final authority once the container stops.
  useEffect(() => {
    if (!container) {
      return;
    }

    let settleTimeoutId: ReturnType<typeof setTimeout>;

    const isOwnOrSuspendedScroll = () =>
      // The reconcile window enforcing state onto the container, our own projection
      // settling, or scrolls while a covering surface owns navigation are not gestures
      isReconcileWindowActive.current ||
      isProgrammaticScrollPending.current ||
      suspendScrollSyncRef.current;

    const reportNearestMedia = () => {
      const nearestIndex = getRestingMediaIndex(container, itemRefs.current);
      if (nearestIndex !== currentMediaRef.current) {
        onSwipeToMediaRef.current(nearestIndex);
      }
    };

    const onScrollSettled = () => {
      if (isReconcileWindowActive.current) {
        return;
      }
      if (isProgrammaticScrollPending.current) {
        // Our projection has settled; don't echo it back into state
        isProgrammaticScrollPending.current = false;
        return;
      }
      if (suspendScrollSyncRef.current) {
        return;
      }
      reportNearestMedia();
    };

    const onScroll = () => {
      clearTimeout(settleTimeoutId);
      settleTimeoutId = setTimeout(onScrollSettled, SCROLL_SETTLE_MS);

      if (!isOwnOrSuspendedScroll()) {
        reportNearestMedia();
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(settleTimeoutId);
      container.removeEventListener('scroll', onScroll);
    };
  }, [container, numberOfMedia]);

  return [setContainer, itemRefs] as const;
};
